import React, { useState, useEffect } from 'react';
import {
    List,
    Datagrid,
    DatagridConfigurable,
    TextField,
    DateField,
    ShowButton,
    useTranslate,
    Filter,
    FilterLiveSearch,
    NumberField,
    TopToolbar,
    SelectColumnsButton,
    useRecordContext,
    useRedirect,
    useNotify,
    useRefresh,
    Button,
    useListContext,
    Button as RaButton,
    FunctionField
} from 'react-admin';
import { Alert, Chip, Card, CardContent, Stack, Box, Typography, Divider, Dialog, DialogTitle, DialogContent, DialogActions, IconButton, TextField as MuiTextField, Tooltip, FormControl, InputLabel, Select, MenuItem, Checkbox, ListItemText, Button as MuiButton } from '@mui/material';
import ErrorIcon from '@mui/icons-material/Error';
import ArchiveIcon from '@mui/icons-material/Archive';
import ReplayIcon from '@mui/icons-material/Replay';
import FileDownloadIcon from '@mui/icons-material/FileDownload';
import { styled } from '@mui/material/styles';
import * as XLSX from 'xlsx';

// Custom Bidder Name Field component with link
const BidderNameField = ({ source }) => {
    const record = useRecordContext();
    const redirect = useRedirect();
    
    if (!record || !record[source]) return null;
    
    const handleClick = (e) => {
        e.stopPropagation();
        // Navigate to the bidder show page using the bidder_id from the serializer
        if (record.bidder_id) {
            redirect('show', 'bidders', record.bidder_id);
        }
    };
    
    return (
        <Typography
            component="a"
            onClick={handleClick}
            sx={{
                color: 'primary.main',
                textDecoration: 'underline',
                cursor: 'pointer',
                '&:hover': {
                    color: 'primary.dark',
                    textDecoration: 'underline',
                }
            }}
        >
            {record[source]}
        </Typography>
    );
};

// Custom Tender Title Field component with link
const TenderTitleField = ({ source }) => {
    const record = useRecordContext();
    const redirect = useRedirect();
    
    if (!record || !record[source]) return null;
    
    const handleClick = (e) => {
        e.stopPropagation();
        // Navigate to the tender show page using the tender_id from the serializer
        if (record.tender_id) {
            redirect('show', 'tenders', record.tender_id);
        }
    };
    
    return (
        <Typography
            component="a"
            onClick={handleClick}
            sx={{
                color: 'primary.main',
                textDecoration: 'underline',
                cursor: 'pointer',
                '&:hover': {
                    color: 'primary.dark',
                    textDecoration: 'underline',
                },
                maxWidth: '300px',
                overflow: 'hidden',
                textOverflow: 'ellipsis',
                whiteSpace: 'nowrap',
                display: 'block'
            }}
            title={record[source]} // Show full title on hover
        >
            {record[source]}
        </Typography>
    );
};

// Combined Bidder Info Field - Name, NIP, Email, Financial Risk in one column
const BidderInfoField = () => {
    const record = useRecordContext();
    const redirect = useRedirect();
    
    if (!record) return null;
    
    const handleNameClick = (e) => {
        e.stopPropagation();
        if (record.bidder_id) {
            redirect('show', 'bidders', record.bidder_id);
        }
    };
    
    return (
        <Box sx={{ display: 'flex', flexDirection: 'column', gap: 0.5, minWidth: 160 }}>
            <Typography
                component="a"
                onClick={handleNameClick}
                sx={{
                    color: 'primary.main',
                    textDecoration: 'underline',
                    cursor: 'pointer',
                    fontWeight: 500,
                    fontSize: '0.875rem',
                    '&:hover': {
                        color: 'primary.dark',
                    }
                }}
            >
                {record.bidder_name || '-'}
            </Typography>
            <Typography sx={{ fontSize: '0.75rem', color: 'text.secondary' }}>
                NIP: {record.bidder_tax_number || '-'}
            </Typography>
            <Typography sx={{ fontSize: '0.75rem', color: 'text.secondary', wordBreak: 'break-all' }}>
                Email: {record.bidder_email || '-'}
            </Typography>
            <Typography sx={{ fontSize: '0.75rem', color: 'text.secondary' }}>
                Ryzyko: {record.bidder_financial_risk_rating ? (
                    <Tooltip 
                        title={record.bidder_financial_risk_description || ''}
                        arrow
                        placement="top"
                    >
                        <Chip 
                            label={record.bidder_financial_risk_rating}
                            color={record.bidder_financial_risk_color || 'default'}
                            size="small"
                            sx={{ fontSize: '0.65rem', height: '18px', ml: 0.5, cursor: 'help' }}
                        />
                    </Tooltip>
                ) : (
                    <span style={{ fontStyle: 'italic' }}>Brak danych</span>
                )}
            </Typography>
            <Typography sx={{ fontSize: '0.75rem', color: 'text.secondary' }}>
                Wiarygodność: {record.bidder_company_credibility_rating != null && record.bidder_company_credibility_rating !== '' ? (
                    <Tooltip 
                        title={record.bidder_company_credibility_description || ''}
                        arrow
                        placement="top"
                    >
                        <Chip 
                            label={record.bidder_company_credibility_rating}
                            color={record.bidder_company_credibility_color || 'default'}
                            size="small"
                            sx={{ fontSize: '0.65rem', height: '18px', ml: 0.5, cursor: 'help' }}
                        />
                    </Tooltip>
                ) : (
                    <span style={{ fontStyle: 'italic' }}>Brak danych</span>
                )}
            </Typography>
        </Box>
    );
};

// Marketing Consents Field - displays three marketing consents with Tak/Nie chips
const MarketingConsentsField = () => {
    const record = useRecordContext();
    const translate = useTranslate();
    
    if (!record) return null;
    
    const renderConsentChip = (value) => {
        if (value === null || value === undefined) {
            return <Chip label="-" color="default" size="small" sx={{ fontSize: '0.7rem', height: '20px' }} />;
        }
        if (value === true || value === 1) {
            return <Chip label={translate('ra.boolean.true', 'Tak')} color="success" size="small" sx={{ fontSize: '0.7rem', height: '20px' }} />;
        }
        return <Chip label={translate('ra.boolean.false', 'Nie')} color="default" size="small" sx={{ fontSize: '0.7rem', height: '20px' }} />;
    };
    
    return (
        <Box sx={{ display: 'flex', flexDirection: 'column', gap: 0.5, minWidth: 180 }}>
            <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
                <Typography sx={{ fontSize: '0.7rem', color: 'text.secondary', fontWeight: 500 }}>
                    Zgoda na email:
                </Typography>
                {renderConsentChip(record.consent_email)}
            </Box>
            <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
                <Typography sx={{ fontSize: '0.7rem', color: 'text.secondary', fontWeight: 500 }}>
                    Zgoda na telefon:
                </Typography>
                {renderConsentChip(record.consent_phone)}
            </Box>
            <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
                <Typography sx={{ fontSize: '0.7rem', color: 'text.secondary', fontWeight: 500 }}>
                    Zgoda na grupę firm:
                </Typography>
                {renderConsentChip(record.consent_group_company)}
            </Box>
        </Box>
    );
};

// Combined Tender Info Field - Title, Notice Number, Wadium and NWU in one column
const TenderInfoField = () => {
    const record = useRecordContext();
    const redirect = useRedirect();
    const translate = useTranslate();
    
    if (!record) return null;
    
    const handleTitleClick = (e) => {
        e.stopPropagation();
        if (record.tender_id) {
            redirect('show', 'tenders', record.tender_id);
        }
    };
    
    // Helper function to render Wadium/NWU status
    const renderStatusChip = (value, label) => {
        if (value === null || value === undefined) {
            return <Chip label="-" color="default" size="small" sx={{ fontSize: '0.7rem', height: '20px' }} />;
        }
        if (Number(value) === 0) {
            return <Chip label={translate('ra.boolean.false', 'Nie')} color="default" size="small" sx={{ fontSize: '0.7rem', height: '20px' }} />;
        }
        if (Number(value) > 0) {
            return <Chip label={translate('ra.boolean.true', 'Tak')} color="success" size="small" sx={{ fontSize: '0.7rem', height: '20px' }} />;
        }
        return <Chip label="-" color="default" size="small" sx={{ fontSize: '0.7rem', height: '20px' }} />;
    };
    
    return (
        <Box sx={{ display: 'flex', flexDirection: 'column', gap: 0.5, minWidth: 200 }}>
            <Typography
                component="a"
                onClick={handleTitleClick}
                sx={{
                    color: 'primary.main',
                    textDecoration: 'underline',
                    cursor: 'pointer',
                    fontWeight: 500,
                    fontSize: '0.875rem',
                    lineHeight: 1.2,
                    '&:hover': {
                        color: 'primary.dark',
                    }
                }}
            >
                {record.tender_title || '-'}
            </Typography>
            <Typography sx={{ fontSize: '0.75rem', color: 'text.secondary' }}>
                {record.tender_notice_number || '-'}
            </Typography>
            <Box sx={{ display: 'flex', gap: 1, alignItems: 'center', flexWrap: 'wrap', mt: 0.5 }}>
                <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
                    <Typography sx={{ fontSize: '0.7rem', color: 'text.secondary', fontWeight: 500 }}>
                        {translate('resources.tenders.fields.bid_bond', 'Wadium')}:
                    </Typography>
                    {renderStatusChip(record.tender_bid_bond)}
                </Box>
                <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
                    <Typography sx={{ fontSize: '0.7rem', color: 'text.secondary', fontWeight: 500 }}>
                        {translate('resources.tenders.fields.nwu', 'NWU')}:
                    </Typography>
                    {renderStatusChip(record.tender_nwu)}
                </Box>
            </Box>
        </Box>
    );
};

// Custom Archive Button component
const ArchiveButton = () => {
    const record = useRecordContext();
    const notify = useNotify();
    const refresh = useRefresh();
    const translate = useTranslate();
    const [showConfirm, setShowConfirm] = React.useState(false);

    if (!record) return null;

    const handleClick = () => {
        setShowConfirm(true);
    };

    const handleConfirm = async () => {
        try {
            // Use the existing archive endpoint from tender workflow
            const token = localStorage.getItem('access_token');
            const API_URL = process.env.REACT_APP_API_URL || 'http://localhost:8000/api';
            
            const response = await fetch(`${API_URL}/tenders/${record.tender_id}/workflow/archive/`, {
                method: 'POST',
                headers: {
                    'Content-Type': 'application/json',
                    'Authorization': `Bearer ${token}`
                },
                body: JSON.stringify({ 
                    workflow_type: 'bidder',
                    bidder_workflow_id: record.id 
                })
            });
            
            if (!response.ok) {
                const errorData = await response.json();
                throw new Error(errorData.detail || 'Failed to archive bidder workflow');
            }
            
            notify('Workflow został zarchiwizowany pomyślnie', { type: 'success' });
            refresh();
        } catch (error) {
            console.error('Error archiving workflow:', error);
            notify(`Błąd podczas archiwizacji: ${error.message || 'Nieznany błąd'}`, { type: 'error' });
        }
        setShowConfirm(false);
    };

    const handleCancel = () => {
        setShowConfirm(false);
    };

    return (
        <>
            <Button
                onClick={handleClick}
                label="Archiwizuj"
                startIcon={<ArchiveIcon />}
                size="small"
                sx={{
                    color: 'warning.main',
                    '&:hover': {
                        backgroundColor: 'warning.light',
                        color: 'warning.contrastText'
                    }
                }}
            />
            <Dialog
                open={showConfirm}
                onClose={handleCancel}
                maxWidth="sm"
                fullWidth
                PaperProps={{
                    sx: {
                        backgroundColor: '#fff',
                        padding: '24px',
                        borderRadius: '8px',
                        boxShadow: '0px 4px 20px rgba(0, 0, 0, 0.1)',
                    }
                }}
            >
                <DialogTitle sx={{ padding: '0 0 16px 0', color: '#333333', fontSize: '1.25rem', fontWeight: 500 }}>
                    Archiwizacja workflow
                </DialogTitle>
                <DialogContent sx={{ padding: '0 0 24px 0', color: '#666666' }}>
                    <Typography>
                        Czy na pewno chcesz zarchiwizować ten workflow oferenta?
                    </Typography>
                    {record.bidder_name && (
                        <Typography sx={{ mt: 1, fontWeight: 500 }}>
                            Oferent: {record.bidder_name}
                        </Typography>
                    )}
                    {record.tender_title && (
                        <Typography sx={{ mt: 1, fontWeight: 500 }}>
                            Przetarg: {record.tender_title}
                        </Typography>
                    )}
                </DialogContent>
                <DialogActions sx={{ padding: 0 }}>
                    <Button onClick={handleCancel} sx={{ textTransform: 'none', fontWeight: 500 }}>
                        Anuluj
                    </Button>
                    <Button onClick={handleConfirm} variant="contained" color="warning" sx={{ textTransform: 'none', fontWeight: 500 }}>
                        Archiwizuj
                    </Button>
                </DialogActions>
            </Dialog>
        </>
    );
};

// Custom Error Message Field component with restart button
const ErrorMessageField = ({ source }) => {
    const record = useRecordContext();
    const notify = useNotify();
    const refresh = useRefresh();
    const redirect = useRedirect();
    const [showConfirm, setShowConfirm] = React.useState(false);

    if (!record || !record[source]) return null;
    
    const handleRestartClick = () => {
        setShowConfirm(true);
    };

    const handleConfirmRestart = async () => {
        try {
            // Use the existing restart endpoint from tender workflow
            const token = localStorage.getItem('access_token');
            const API_URL = process.env.REACT_APP_API_URL || 'http://localhost:8000/api';
            
            const response = await fetch(`${API_URL}/tenders/${record.tender_id}/workflow/restart-bidder/`, {
                method: 'POST',
                headers: {
                    'Content-Type': 'application/json',
                    'Authorization': `Bearer ${token}`
                },
                body: JSON.stringify({ 
                    bidder_workflow_id: record.id,
                    block_name: record.current_block || 'AnalizaOferentaBlock'
                })
            });
            
            if (!response.ok) {
                const errorData = await response.json();
                throw new Error(errorData.detail || 'Failed to restart bidder workflow');
            }
            
            notify('Workflow został ponownie uruchomiony pomyślnie', { type: 'success' });
            refresh();
        } catch (error) {
            console.error('Error restarting workflow:', error);
            notify(`Błąd podczas ponownego uruchamiania: ${error.message || 'Nieznany błąd'}`, { type: 'error' });
        }
        setShowConfirm(false);
    };

    const handleCancelRestart = () => {
        setShowConfirm(false);
    };
    
    // Parse error_message - serializer returns array or we need to parse JSON string
    const getErrorMessages = () => {
        if (!record[source]) return null;
        
        // If it's already an array (from serializer), return it
        if (Array.isArray(record[source])) {
            return record[source];
        }
        
        // Try to parse as JSON string (fallback for old data format)
        try {
            const parsed = JSON.parse(record[source]);
            if (Array.isArray(parsed)) {
                return parsed;
            }
            return [String(parsed)];
        } catch (e) {
            // If not JSON, treat as single string message
            return [String(record[source])];
        }
    };
    
    const errorMessages = getErrorMessages();
    
    const handleErrorClick = (e) => {
        e.stopPropagation();
        // Only prevent default for left click - allow right click and middle click to work normally
        if (e.button === 0 && !e.ctrlKey && !e.metaKey) {
            e.preventDefault();
            if (record.tender_id && record.id && record.current_block) {
                // Use path-based routing for tabs in react-admin: /show/workflow instead of ?tab=workflow
                const url = `/tenders/${record.tender_id}/show/workflow?bidderWorkflow=${record.id}&block=${encodeURIComponent(record.current_block)}`;
                console.log('Redirecting to:', url);
                redirect(url);
            } else {
                console.log('Missing data:', { tender_id: record.tender_id, id: record.id, current_block: record.current_block });
            }
        }
    };
    
    // Generate URL for the link
    const getErrorLink = () => {
        if (record.tender_id && record.id && record.current_block) {
            // Use the same URL format as redirect - React Admin will handle routing
            return `/tenders/${record.tender_id}/show/workflow?bidderWorkflow=${record.id}&block=${encodeURIComponent(record.current_block)}`;
        }
        return null;
    };
    
    return (
        <>
            <Box sx={{ display: 'flex', alignItems: 'flex-start', gap: 1, minHeight: '60px', width: '100%' }}>
                <Box
                    component={getErrorLink() ? "a" : "div"}
                    href={getErrorLink()}
                    sx={{ 
                        flex: 1,
                        maxWidth: '320px',
                        minWidth: '200px',
                        fontSize: '0.875rem',
                        lineHeight: 1.4,
                        color: 'text.primary',
                        wordBreak: 'break-word',
                        whiteSpace: 'normal',
                        cursor: record.tender_id && record.id && record.current_block ? 'pointer' : 'default',
                        userSelect: 'none',
                        textDecoration: 'none'
                    }}
                    onClick={handleErrorClick}
                    title={record.tender_id && record.id && record.current_block ? 'Kliknij, aby przejść do szczegółów workflow' : ''}
                >
                    {errorMessages && errorMessages.map((error, idx) => (
                        <Typography 
                            key={idx} 
                            sx={{ 
                                fontSize: '0.875rem',
                                color: 'error.main',
                                mb: idx < errorMessages.length - 1 ? 0.5 : 0,
                                wordBreak: 'break-word',
                                lineHeight: 1.4,
                                '&:hover': record.tender_id && record.id && record.current_block ? {
                                    textDecoration: 'underline'
                                } : {}
                            }}
                        >
                            – {error}
                        </Typography>
                    ))}
                </Box>
                <IconButton 
                    size="small" 
                    onClick={handleRestartClick}
                    sx={{ 
                        mt: 0.5,
                        color: 'info.main',
                        '&:hover': {
                            backgroundColor: 'info.light',
                            color: 'info.contrastText'
                        }
                    }}
                    title="Uruchom ponownie workflow"
                >
                    <ReplayIcon />
                </IconButton>
            </Box>
            
            {/* Restart Confirmation Dialog */}
            <Dialog
                open={showConfirm}
                onClose={handleCancelRestart}
                maxWidth="sm"
                fullWidth
                PaperProps={{
                    sx: {
                        backgroundColor: '#fff',
                        padding: '24px',
                        borderRadius: '8px',
                        boxShadow: '0px 4px 20px rgba(0, 0, 0, 0.1)',
                    }
                }}
            >
                <DialogTitle sx={{ padding: '0 0 16px 0', color: '#333333', fontSize: '1.25rem', fontWeight: 500 }}>
                    Ponowne uruchomienie workflow
                </DialogTitle>
                <DialogContent sx={{ padding: '0 0 24px 0', color: '#666666' }}>
                    <Typography>
                        Czy na pewno chcesz ponownie uruchomić ten workflow oferenta?
                    </Typography>
                    {record.bidder_name && (
                        <Typography sx={{ mt: 1, fontWeight: 500 }}>
                            Oferent: {record.bidder_name}
                        </Typography>
                    )}
                    {record.tender_title && (
                        <Typography sx={{ mt: 1, fontWeight: 500 }}>
                            Przetarg: {record.tender_title}
                        </Typography>
                    )}
                    {record.current_block && (
                        <Typography sx={{ mt: 1, fontWeight: 500 }}>
                            Blok: {record.current_block}
                        </Typography>
                    )}
                    <Typography sx={{ mt: 2, color: '#ff9800', fontWeight: 500 }}>
                        Uwaga: To spowoduje zresetowanie postępu od tego kroku w dół.
                    </Typography>
                </DialogContent>
                <DialogActions sx={{ padding: 0 }}>
                    <Button onClick={handleCancelRestart} sx={{ textTransform: 'none', fontWeight: 500 }}>
                        Anuluj
                    </Button>
                    <Button onClick={handleConfirmRestart} variant="contained" color="info" sx={{ textTransform: 'none', fontWeight: 500 }}>
                        Uruchom ponownie
                    </Button>
                </DialogActions>
            </Dialog>
        </>
    );
};

// Custom Offer Status Field component (same as in bidderOfferLeads)
const OfferStatusField = ({ source }) => {
    const record = useRecordContext();
    if (!record || !record[source]) return null;
    
    const status = record[source];
    let color = 'default';
    let label = status;
    
    switch (status) {
        case 'LEAD':
            color = 'success';
            label = 'Lead';
            break;
        case 'REJECTED':
            color = 'error';
            label = 'Odrzucony';
            break;
        case 'PARTICIPANT':
            color = 'info';
            label = 'Uczestnik';
            break;
        default:
            color = 'default';
            label = status || '-';
    }
    
    return <Chip label={label} color={color} size="small" />;
};

// Combined Workflow Info Field - Status and Current Block in one column
const WorkflowInfoField = () => {
    const record = useRecordContext();
    const translate = useTranslate();
    
    // Get workflow status
    const getWorkflowStatus = () => {
        if (!record || !record.status) {
            return { color: 'default', label: 'Brak statusu' };
        }
        
        const status = record.status;
        switch (status) {
            case 'failed':
                return { color: 'error', label: 'Nieudany' };
            case 'running':
                return { color: 'warning', label: 'W trakcie' };
            case 'done':
                return { color: 'success', label: 'Zakończony' };
            case 'pending':
                return { color: 'info', label: 'Oczekujący' };
            case 'waiting':
                return { color: 'warning', label: 'Oczekuje' };
            case 'archived':
                return { color: 'default', label: 'Zarchiwizowany' };
            default:
                return { color: 'default', label: status || '-' };
        }
    };
    
    const statusInfo = getWorkflowStatus();
    const currentBlock = record?.current_block;
    
    return (
        <Box sx={{ display: 'flex', flexDirection: 'column', gap: 0.5, minWidth: '200px', maxWidth: '280px' }}>
            {/* Status Chip */}
            <Chip label={statusInfo.label} color={statusInfo.color} size="small" />
            
            {/* Current Block as plain text with label - only show if workflow is not completed */}
            {currentBlock && statusInfo.label !== 'Zakończony' && (
                <Box sx={{ mt: 0.5 }}>
                    <Typography variant="body2" sx={{ 
                        fontSize: '0.75rem',
                        color: 'text.secondary',
                        mb: 0.25
                    }}>
                        Krok:
                    </Typography>
                    <Typography variant="body2" sx={{ 
                        fontSize: '0.875rem',
                        color: 'text.primary',
                        wordBreak: 'break-word',
                        lineHeight: 1.3
                    }}>
                        {currentBlock}
                    </Typography>
                </Box>
            )}
        </Box>
    );
};

const StyledList = styled(List)({
  '& .MuiPaper-root': {
    padding: '12px 16px !important',
    border: '1px solid #e0e0e0',
    backgroundColor: '#fff',
  },
  '& .MuiToolbar-root': {
    minHeight: '40px !important',
    paddingLeft: 0,
    paddingRight: 0,
    marginBottom: '0px',
  }
});

const FailedBidderWorkflowFilter = (props) => {
    const translate = useTranslate();
    return (
        <Filter {...props}>
            <FilterLiveSearch source="bidder_name" label="Nazwa oferenta" />
            <FilterLiveSearch source="bidder_tax_number" label="NIP" />
            <FilterLiveSearch source="bidder_email" label="Email" />
            <FilterLiveSearch source="tender_notice_number" label="Numer ogłoszenia" />
            <FilterLiveSearch source="tender_title" label="Tytuł przetargu" />
            <FilterLiveSearch source="current_block" label="Aktualny proces" />
            <FilterLiveSearch source="error_message" label="Komunikat błędu" />
        </Filter>
    );
};

// Export CSV Button
const ExportCSVButton = () => {
  const { filterValues, sort } = useListContext();
  const [loading, setLoading] = useState(false);
  const notify = useNotify();
  const translate = useTranslate();

  const handleExport = async () => {
    setLoading(true);
    try {
      // Build base query parameters to match current filters and sorting
      const baseParams = new URLSearchParams();
      
      // Add filters
      Object.entries(filterValues || {}).forEach(([key, value]) => {
        if (value !== undefined && value !== null && value !== '') {
          baseParams.append(key, value);
        }
      });
      
      // Add sorting
      if (sort?.field) {
        baseParams.append('_sort', sort.field);
        baseParams.append('_order', sort.order?.toLowerCase() || 'desc');
      }
      
      // Set max page size (50 is the backend limit)
      baseParams.append('_limit', '50');
      
      // Fetch all pages
      const apiUrl = process.env.REACT_APP_API_URL || 'http://localhost:8000/api';
      let allData = [];
      let currentPage = 1;
      let hasMore = true;
      
      while (hasMore) {
        const params = new URLSearchParams(baseParams);
        params.append('_page', currentPage.toString());
        
        const response = await fetch(`${apiUrl}/workflow/failed-bidder-workflows/?${params.toString()}`, {
          headers: {
            'Authorization': `Bearer ${localStorage.getItem('access_token')}`,
            'Content-Type': 'application/json',
          },
        });
        
        if (!response.ok) {
          throw new Error('Export failed');
        }
        
        const result = await response.json();
        const pageData = result.results || [];
        
        if (pageData.length === 0) {
          hasMore = false;
        } else {
          allData = [...allData, ...pageData];
          // Check if there are more pages
          const totalCount = result.count || 0;
          if (allData.length >= totalCount || pageData.length < 50) {
            hasMore = false;
          } else {
            currentPage++;
          }
        }
      }
      
      const data = allData;
      
      if (!data || data.length === 0) {
        notify('Brak danych do eksportu', { type: 'warning' });
        return;
      }
      
      // Flatten the data for CSV
      const flattenedData = data.map(record => ({
        id: record.id || '',
        bidder_name: record.bidder_name || '',
        bidder_tax_number: record.bidder_tax_number || '',
        bidder_email: record.bidder_email || '',
        tender_title: (record.tender_title || '').replace(/"/g, '""'),
        tender_notice_number: record.tender_notice_number || '',
        tender_deadline: record.tender_deadline || '',
        current_block: record.current_block || '',
        status: record.status || '',
        error_message: Array.isArray(record.error_message) 
          ? record.error_message.join('; ').replace(/"/g, '""')
          : (record.error_message || '').replace(/"/g, '""'),
        updated_at: record.updated_at || '',
      }));
      
      // Create CSV manually
      const headers = [
        'ID', 'Nazwa oferenta', 'NIP', 'Email', 'Tytuł przetargu', 
        'Numer ogłoszenia', 'Termin składania ofert', 'Aktualny proces', 
        'Status', 'Komunikat błędu', 'Ostatnia aktualizacja'
      ];
      
      const csvContent = [
        headers.join(','),
        ...flattenedData.map(row => [
          row.id, 
          `"${row.bidder_name}"`, 
          row.bidder_tax_number, 
          row.bidder_email,
          `"${row.tender_title}"`,
          row.tender_notice_number,
          row.tender_deadline,
          row.current_block,
          row.status,
          `"${row.error_message}"`,
          row.updated_at
        ].join(','))
      ].join('\n');
      
      // Download the CSV file
      const blob = new Blob([csvContent], { type: 'text/csv;charset=utf-8;' });
      const link = document.createElement('a');
      const url = URL.createObjectURL(blob);
      link.setAttribute('href', url);
      link.setAttribute('download', `failed_bidder_workflows_${new Date().toISOString().split('T')[0]}.csv`);
      link.style.visibility = 'hidden';
      document.body.appendChild(link);
      link.click();
      document.body.removeChild(link);
      
      notify(`Eksportowano ${data.length} rekordów`, { type: 'success' });
    } catch (error) {
      console.error('Export error:', error);
      notify('Błąd podczas eksportu', { type: 'error' });
    } finally {
      setLoading(false);
    }
  };

  return (
    <RaButton 
      label={loading ? 'Eksportowanie...' : "Export CSV"} 
      onClick={handleExport}
      disabled={loading}
      startIcon={<FileDownloadIcon />}
      sx={{
        backgroundColor: '#E31837',
        color: '#ffffff',
        '&:hover': {
          backgroundColor: '#C41430',
        },
        '& .MuiSvgIcon-root': {
          color: '#ffffff',
        },
      }}
    />
  );
};

// Export Excel Button
const ExportExcelButton = () => {
  const { filterValues, sort } = useListContext();
  const [loading, setLoading] = useState(false);
  const notify = useNotify();
  const translate = useTranslate();

  const handleExport = async () => {
    setLoading(true);
    try {
      // Build base query parameters to match current filters and sorting
      const baseParams = new URLSearchParams();
      
      // Add filters
      Object.entries(filterValues || {}).forEach(([key, value]) => {
        if (value !== undefined && value !== null && value !== '') {
          baseParams.append(key, value);
        }
      });
      
      // Add sorting
      if (sort?.field) {
        baseParams.append('_sort', sort.field);
        baseParams.append('_order', sort.order?.toLowerCase() || 'desc');
      }
      
      // Set max page size (50 is the backend limit)
      baseParams.append('_limit', '50');
      
      // Fetch all pages
      const apiUrl = process.env.REACT_APP_API_URL || 'http://localhost:8000/api';
      let allData = [];
      let currentPage = 1;
      let hasMore = true;
      
      while (hasMore) {
        const params = new URLSearchParams(baseParams);
        params.append('_page', currentPage.toString());
        
        const response = await fetch(`${apiUrl}/workflow/failed-bidder-workflows/?${params.toString()}`, {
          headers: {
            'Authorization': `Bearer ${localStorage.getItem('access_token')}`,
            'Content-Type': 'application/json',
          },
        });
        
        if (!response.ok) {
          throw new Error('Export failed');
        }
        
        const result = await response.json();
        const pageData = result.results || [];
        
        if (pageData.length === 0) {
          hasMore = false;
        } else {
          allData = [...allData, ...pageData];
          // Check if there are more pages
          const totalCount = result.count || 0;
          if (allData.length >= totalCount || pageData.length < 50) {
            hasMore = false;
          } else {
            currentPage++;
          }
        }
      }
      
      const data = allData;
      
      if (!data || data.length === 0) {
        notify('Brak danych do eksportu', { type: 'warning' });
        return;
      }
      
      // Flatten the data for Excel
      const flattenedData = data.map(record => ({
        'ID': record.id || '',
        'Nazwa oferenta': record.bidder_name || '',
        'NIP': record.bidder_tax_number || '',
        'Email': record.bidder_email || '',
        'Tytuł przetargu': record.tender_title || '',
        'Numer ogłoszenia': record.tender_notice_number || '',
        'Termin składania ofert': record.tender_deadline || '',
        'Aktualny proces': record.current_block || '',
        'Status': record.status || '',
        'Komunikat błędu': Array.isArray(record.error_message) 
          ? record.error_message.join('; ')
          : (record.error_message || ''),
        'Ostatnia aktualizacja': record.updated_at || '',
      }));
      
      // Create Excel file
      const worksheet = XLSX.utils.json_to_sheet(flattenedData);
      const workbook = XLSX.utils.book_new();
      XLSX.utils.book_append_sheet(workbook, worksheet, 'Failed Workflows');
      XLSX.writeFile(workbook, `failed_bidder_workflows_${new Date().toISOString().split('T')[0]}.xlsx`);
      
      notify(`Eksportowano ${data.length} rekordów`, { type: 'success' });
    } catch (error) {
      console.error('Export error:', error);
      notify('Błąd podczas eksportu', { type: 'error' });
    } finally {
      setLoading(false);
    }
  };

  return (
    <RaButton 
      label={loading ? 'Eksportowanie...' : "Export do Excel"} 
      onClick={handleExport}
      disabled={loading}
      startIcon={<FileDownloadIcon />}
      sx={{
        backgroundColor: '#E31837',
        color: '#ffffff',
        '&:hover': {
          backgroundColor: '#C41430',
        },
        '& .MuiSvgIcon-root': {
          color: '#ffffff',
        },
      }}
    />
  );
};

const ListActions = () => {
  return (
    <TopToolbar>             
      <SelectColumnsButton />      
      <ExportCSVButton />
      <ExportExcelButton />
    </TopToolbar>
  );
};

const FailedBidderWorkflowFilterSidebar = () => {
  const translate = useTranslate();
  const { setFilters, filterValues } = useListContext();
  
  const getBooleanSelectValue = (value) => {
    if (value === true || value === 'true') return 'true';
    if (value === false || value === 'false') return 'false';
    return '';
  };
  
  const [filterKey, setFilterKey] = useState(Date.now());
  const [from, setFrom] = useState('');
  const [to, setTo] = useState('');
  const [offerDateFrom, setOfferDateFrom] = useState('');
  const [offerDateTo, setOfferDateTo] = useState('');
  const [nwu, setNwu] = useState(getBooleanSelectValue(filterValues.is_nwu));
  const [wadium, setWadium] = useState(getBooleanSelectValue(filterValues.is_wadium));
  const [missingNip, setMissingNip] = useState(getBooleanSelectValue(filterValues.missing_tax_number));
  const [missingEmail, setMissingEmail] = useState(getBooleanSelectValue(filterValues.missing_email));
  const [missingPwg, setMissingPwg] = useState(getBooleanSelectValue(filterValues.missing_pwg));
  const [gracePeriod, setGracePeriod] = useState(getBooleanSelectValue(filterValues.is_in_grace_period));
  const [consentEmail, setConsentEmail] = useState(getBooleanSelectValue(filterValues.consent_email));
  const [consentPhone, setConsentPhone] = useState(getBooleanSelectValue(filterValues.consent_phone));
  const [consentGroupCompany, setConsentGroupCompany] = useState(getBooleanSelectValue(filterValues.consent_group_company));
  const [financialRisk, setFinancialRisk] = useState(() => {
    if (filterValues.financial_risk_rating) {
      return filterValues.financial_risk_rating.split(',').map(v => v.trim()).filter(v => v);
    }
    return [];
  });
  const [companyCredibility, setCompanyCredibility] = useState(() => {
    if (filterValues.company_credibility_rating) {
      return filterValues.company_credibility_rating.split(',').map(v => v.trim()).filter(v => v);
    }
    return [];
  });

  useEffect(() => {
    setNwu(getBooleanSelectValue(filterValues.is_nwu));
  }, [filterValues.is_nwu]);

  useEffect(() => {
    setWadium(getBooleanSelectValue(filterValues.is_wadium));
  }, [filterValues.is_wadium]);

  useEffect(() => {
    setMissingNip(getBooleanSelectValue(filterValues.missing_tax_number));
  }, [filterValues.missing_tax_number]);

  useEffect(() => {
    setMissingEmail(getBooleanSelectValue(filterValues.missing_email));
  }, [filterValues.missing_email]);

  useEffect(() => {
    setMissingPwg(getBooleanSelectValue(filterValues.missing_pwg));
  }, [filterValues.missing_pwg]);

  useEffect(() => {
    setGracePeriod(getBooleanSelectValue(filterValues.is_in_grace_period));
  }, [filterValues.is_in_grace_period]);

  useEffect(() => {
    if (filterValues.financial_risk_rating) {
      const values = filterValues.financial_risk_rating.split(',').map(v => v.trim()).filter(v => v);
      setFinancialRisk(values);
    } else {
      setFinancialRisk([]);
    }
  }, [filterValues.financial_risk_rating]);

  useEffect(() => {
    if (filterValues.company_credibility_rating) {
      const values = filterValues.company_credibility_rating.split(',').map(v => v.trim()).filter(v => v);
      setCompanyCredibility(values);
    } else {
      setCompanyCredibility([]);
    }
  }, [filterValues.company_credibility_rating]);

  useEffect(() => {
    if (filterValues.updated_at_gte) {
      try {
        const date = new Date(filterValues.updated_at_gte);
        const year = date.getFullYear();
        const month = String(date.getMonth() + 1).padStart(2, '0');
        const day = String(date.getDate()).padStart(2, '0');
        setFrom(`${year}-${month}-${day}`);
      } catch (e) {
        setFrom('');
      }
    } else {
      setFrom('');
    }
    
    if (filterValues.updated_at_lte) {
      try {
        const date = new Date(filterValues.updated_at_lte);
        const year = date.getFullYear();
        const month = String(date.getMonth() + 1).padStart(2, '0');
        const day = String(date.getDate()).padStart(2, '0');
        setTo(`${year}-${month}-${day}`);
      } catch (e) {
        setTo('');
      }
    } else {
      setTo('');
    }
  }, [filterValues.updated_at_gte, filterValues.updated_at_lte]);

  useEffect(() => {
    if (filterValues.offer_date_gte) {
      try {
        const date = new Date(filterValues.offer_date_gte);
        const year = date.getFullYear();
        const month = String(date.getMonth() + 1).padStart(2, '0');
        const day = String(date.getDate()).padStart(2, '0');
        setOfferDateFrom(`${year}-${month}-${day}`);
      } catch (e) {
        setOfferDateFrom('');
      }
    } else {
      setOfferDateFrom('');
    }
    
    if (filterValues.offer_date_lte) {
      try {
        const date = new Date(filterValues.offer_date_lte);
        const year = date.getFullYear();
        const month = String(date.getMonth() + 1).padStart(2, '0');
        const day = String(date.getDate()).padStart(2, '0');
        setOfferDateTo(`${year}-${month}-${day}`);
      } catch (e) {
        setOfferDateTo('');
      }
    } else {
      setOfferDateTo('');
    }
  }, [filterValues.offer_date_gte, filterValues.offer_date_lte]);

  useEffect(() => {
    const hasAnyFilter = 
      filterValues.bidder_name || 
      filterValues.bidder_tax_number || 
      filterValues.bidder_email ||
      filterValues.bidder_registration_number ||
      filterValues.bidder_krs ||
      filterValues.bidder_pkd ||
      filterValues.bidder_pkd_others ||
      filterValues.bidder_city ||
      filterValues.bidder_municipality ||
      filterValues.bidder_province ||
      filterValues.tender_notice_number ||
      filterValues.tender_title ||
      filterValues.is_nwu !== undefined ||
      filterValues.is_wadium !== undefined ||
      filterValues.missing_tax_number !== undefined ||
      filterValues.missing_email !== undefined ||
      filterValues.missing_pwg !== undefined ||
      filterValues.is_in_grace_period !== undefined ||
      filterValues.consent_email !== undefined ||
      filterValues.consent_phone !== undefined ||
      filterValues.consent_group_company !== undefined ||
      filterValues.is_current_customer !== undefined ||
      filterValues.financial_risk_rating ||
      filterValues.company_credibility_rating ||
      filterValues.updated_at_gte ||
      filterValues.updated_at_lte ||
      filterValues.offer_date_gte ||
      filterValues.offer_date_lte;
    
    if (!hasAnyFilter && Object.keys(filterValues).length === 0) {
      setFilterKey(Date.now());
    }
  }, [filterValues]);
  const getPolfundCustomerValue = () => {
    if (filterValues.is_current_customer === undefined || filterValues.is_current_customer === null) {
      return '';
    }
    return filterValues.is_current_customer === true || filterValues.is_current_customer === 'true' ? 'true' : 'false';
  };

  const handleDateChange = (type, value) => {
    const newFilters = { ...filterValues };
    
    if (type === 'from') {
      setFrom(value);
      if (value === '') {
        delete newFilters.updated_at_gte;
      } else {
        const [year, month, day] = value.split('-').map(Number);
        const date = new Date(year, month - 1, day, 0, 0, 0, 0);
        newFilters.updated_at_gte = date.toISOString();
      }
    }
    
    if (type === 'to') {
      setTo(value);
      if (value === '') {
        delete newFilters.updated_at_lte;
      } else {
        const [year, month, day] = value.split('-').map(Number);
        const date = new Date(year, month - 1, day, 23, 59, 59, 999);
        newFilters.updated_at_lte = date.toISOString();
      }
    }
    
    setFilters(newFilters, {});
  };

  const handleOfferDateChange = (type, value) => {
    const newFilters = { ...filterValues };
    
    if (type === 'from') {
      setOfferDateFrom(value);
      if (value === '') {
        delete newFilters.offer_date_gte;
      } else {
        const [year, month, day] = value.split('-').map(Number);
        const date = new Date(year, month - 1, day, 0, 0, 0, 0);
        newFilters.offer_date_gte = date.toISOString();
      }
    }
    
    if (type === 'to') {
      setOfferDateTo(value);
      if (value === '') {
        delete newFilters.offer_date_lte;
      } else {
        const [year, month, day] = value.split('-').map(Number);
        const date = new Date(year, month - 1, day, 23, 59, 59, 999);
        newFilters.offer_date_lte = date.toISOString();
      }
    }
    
    setFilters(newFilters, {});
  };

  const createBooleanFilterHandler = (key, setter) => (event) => {
    const value = event.target.value;
    setter(value);
    
    const newFilters = { ...filterValues };
    if (value === '') {
      delete newFilters[key];
    } else {
      newFilters[key] = value === 'true';
    }
    setFilters(newFilters, {});
  };
  
  const handleNwuChange = createBooleanFilterHandler('is_nwu', setNwu);
  const handleWadiumChange = createBooleanFilterHandler('is_wadium', setWadium);
  const handleMissingNipChange = createBooleanFilterHandler('missing_tax_number', setMissingNip);
  const handleMissingEmailChange = createBooleanFilterHandler('missing_email', setMissingEmail);
  const handleMissingPwgChange = createBooleanFilterHandler('missing_pwg', setMissingPwg);
  const handleGracePeriodChange = createBooleanFilterHandler('is_in_grace_period', setGracePeriod);
  const handleConsentEmailChange = createBooleanFilterHandler('consent_email', setConsentEmail);
  const handleConsentPhoneChange = createBooleanFilterHandler('consent_phone', setConsentPhone);
  const handleConsentGroupCompanyChange = createBooleanFilterHandler('consent_group_company', setConsentGroupCompany);

  const handlePolfundCustomerChange = (event) => {
    const value = event.target.value;
    const newFilters = { ...filterValues };
    
    if (value === '' || value === null || value === undefined) {
      delete newFilters.is_current_customer;
    } else {
      newFilters.is_current_customer = value === 'true';
    }
    
    setFilters(newFilters, {});
  };

  const handleFinancialRiskChange = (event) => {
    const value = event.target.value;
    const selectedValues = typeof value === 'string' ? value.split(',') : value;
    setFinancialRisk(selectedValues);
    
    const newFilters = { ...filterValues };
    if (!selectedValues || selectedValues.length === 0) {
      delete newFilters.financial_risk_rating;
    } else {
      newFilters.financial_risk_rating = selectedValues.join(',');
    }
    setFilters(newFilters, {});
  };

  const handleCompanyCredibilityChange = (event) => {
    const value = event.target.value;
    const selectedValues = typeof value === 'string' ? value.split(',') : value;
    setCompanyCredibility(selectedValues);
    
    const newFilters = { ...filterValues };
    if (!selectedValues || selectedValues.length === 0) {
      delete newFilters.company_credibility_rating;
    } else {
      newFilters.company_credibility_rating = selectedValues.join(',');
    }
    setFilters(newFilters, {});
  };

  const handleResetFilters = () => {
    setFrom('');
    setTo('');
    setOfferDateFrom('');
    setOfferDateTo('');
    setNwu('');
    setWadium('');
    setMissingNip('');
    setMissingEmail('');
    setMissingPwg('');
    setGracePeriod('');
    setConsentEmail('');
    setConsentPhone('');
    setConsentGroupCompany('');
    setFinancialRisk([]);
    setCompanyCredibility([]);
    
    setFilterKey(Date.now());
    
    requestAnimationFrame(() => {
      const allFilterFields = {
        bidder_name: '',
        bidder_tax_number: '',
        bidder_email: '',
        bidder_registration_number: '',
        bidder_krs: '',
        bidder_pkd: '',
        bidder_pkd_others: '',
        bidder_city: '',
        bidder_municipality: '',
        bidder_province: '',
        tender_notice_number: '',
        tender_title: '',
        current_block: '',
        error_message: '',
        is_nwu: '',
        is_wadium: '',
        missing_tax_number: '',
        missing_email: '',
        missing_pwg: '',
        is_in_grace_period: '',
        consent_email: '',
        consent_phone: '',
        consent_group_company: '',
        is_current_customer: '',
        financial_risk_rating: '',
        company_credibility_rating: '',
        updated_at_gte: '',
        updated_at_lte: '',
        offer_date_gte: '',
        offer_date_lte: '',
      };
      
      setFilters(allFilterFields, {});
      
      requestAnimationFrame(() => {
        setFilters({}, {});
      });
    });
  };

  return (
    <Card sx={{ order: -1, mr: 2, mt: 9, minWidth: 250, maxWidth: 300, boxShadow: 0, borderRadius: 2 }}>
      <CardContent>
        <Stack spacing={1.5}>
          <Typography variant="subtitle2" sx={{ fontWeight: 600, color: 'text.primary', mt: 1, mb: 0.5 }}>
            Filtry po oferenci
          </Typography>
          
          <FilterLiveSearch key={`bidder_name-${filterKey}`} source="bidder_name" label={translate('resources.bidders.fields.name', 'Nazwa oferenta')} />
          <FilterLiveSearch key={`bidder_tax_number-${filterKey}`} source="bidder_tax_number" label={translate('resources.bidders.fields.tax_number', 'NIP')} />
          <FilterLiveSearch key={`bidder_email-${filterKey}`} source="bidder_email" label="Email" />
          <FilterLiveSearch key={`bidder_registration_number-${filterKey}`} source="bidder_registration_number" label={translate('resources.bidders.fields.registration_number', 'REGON')} />
          <FilterLiveSearch key={`bidder_krs-${filterKey}`} source="bidder_krs" label={translate('resources.bidders.fields.krs', 'KRS')} />
          <FilterLiveSearch key={`bidder_pkd-${filterKey}`} source="bidder_pkd" label={translate('resources.bidders.fields.pkd', 'PKD')} />
          <FilterLiveSearch key={`bidder_pkd_others-${filterKey}`} source="bidder_pkd_others" label="Pozostałe kody PKD" />
          <FilterLiveSearch key={`bidder_city-${filterKey}`} source="bidder_city" label={translate('resources.bidders.fields.city', 'Miasto')} />
          <FilterLiveSearch key={`bidder_municipality-${filterKey}`} source="bidder_municipality" label={translate('resources.bidders.fields.municipality', 'Gmina')} />
          <FilterLiveSearch key={`bidder_province-${filterKey}`} source="bidder_province" label={translate('resources.bidders.fields.province', 'Województwo')} />
          
          <FormControl fullWidth size="small" sx={{ mt: 0.5 }}>
            <InputLabel id="polfund-customer-filter-label" sx={{ fontSize: '0.875rem' }}>
              Czy klient Polfund
            </InputLabel>
            <Select
              labelId="polfund-customer-filter-label"
              value={getPolfundCustomerValue()}
              onChange={handlePolfundCustomerChange}
              label="Czy klient Polfund"
              sx={{ fontSize: '0.875rem' }}
            >
              <MenuItem value="">
                <em>Wszystkie</em>
              </MenuItem>
              <MenuItem value="true">TAK</MenuItem>
              <MenuItem value="false">NIE</MenuItem>
            </Select>
          </FormControl>

          <FormControl fullWidth size="small" sx={{ mt: 1 }}>
            <InputLabel id="financial-risk-filter-label" sx={{ fontSize: '0.875rem' }}>
              Skala ryzyka finansowego
            </InputLabel>
            <Select
              labelId="financial-risk-filter-label"
              multiple
              value={financialRisk}
              onChange={handleFinancialRiskChange}
              label="Skala ryzyka finansowego"
              sx={{ fontSize: '0.875rem' }}
              renderValue={(selected) => {
                if (selected.length === 0) return '';
                if (selected.length === 1) {
                  const labels = {
                    '5': '5',
                    '4+': '4+',
                    '4': '4',
                    '4-': '4-',
                    '3+': '3+',
                    '3': '3',
                    '3-': '3-',
                    '2': '2',
                    '?': '?',
                    'none': 'Brak danych'
                  };
                  return labels[selected[0]] || selected[0];
                }
                return `${selected.length} wybrane`;
              }}
            >
              <MenuItem value="5">
                <Checkbox checked={financialRisk.indexOf('5') > -1} />
                <ListItemText primary="5 - Znikome ryzyko finansowe" />
              </MenuItem>
              <MenuItem value="4+">
                <Checkbox checked={financialRisk.indexOf('4+') > -1} />
                <ListItemText primary="4+ - Minimalne ryzyko finansowe" />
              </MenuItem>
              <MenuItem value="4">
                <Checkbox checked={financialRisk.indexOf('4') > -1} />
                <ListItemText primary="4 - Bardzo niskie ryzyko finansowe" />
              </MenuItem>
              <MenuItem value="4-">
                <Checkbox checked={financialRisk.indexOf('4-') > -1} />
                <ListItemText primary="4- - Niskie ryzyko finansowe" />
              </MenuItem>
              <MenuItem value="3+">
                <Checkbox checked={financialRisk.indexOf('3+') > -1} />
                <ListItemText primary="3+ - Średnie ryzyko finansowe" />
              </MenuItem>
              <MenuItem value="3">
                <Checkbox checked={financialRisk.indexOf('3') > -1} />
                <ListItemText primary="3 - Wysokie ryzyko finansowe" />
              </MenuItem>
              <MenuItem value="3-">
                <Checkbox checked={financialRisk.indexOf('3-') > -1} />
                <ListItemText primary="3- - Bardzo wysokie ryzyko finansowe" />
              </MenuItem>
              <MenuItem value="2">
                <Checkbox checked={financialRisk.indexOf('2') > -1} />
                <ListItemText primary="2 - Krytycznie wysokie ryzyko finansowe" />
              </MenuItem>
              <MenuItem value="?">
                <Checkbox checked={financialRisk.indexOf('?') > -1} />
                <ListItemText primary="? - Brak danych do oceny finansowej" />
              </MenuItem>
              <MenuItem value="none">
                <Checkbox checked={financialRisk.indexOf('none') > -1} />
                <ListItemText primary="Brak danych" />
              </MenuItem>
            </Select>
          </FormControl>

          <FormControl fullWidth size="small" sx={{ mt: 1 }}>
            <InputLabel id="company-credibility-filter-label" sx={{ fontSize: '0.875rem' }}>
              Skala wiarygodności
            </InputLabel>
            <Select
              labelId="company-credibility-filter-label"
              multiple
              value={companyCredibility}
              onChange={handleCompanyCredibilityChange}
              label="Skala wiarygodności"
              sx={{ fontSize: '0.875rem' }}
              renderValue={(selected) => {
                if (selected.length === 0) return '';
                if (selected.length === 1) {
                  const labels = {
                    '5': '5 - Współpraca wskazana',
                    '4': '4 - Zalecamy współpracę',
                    '3': '3 - Zalecamy ostrożność',
                    '2': '2 - Zalecamy szczególną ostrożność',
                    'none': 'Brak danych'
                  };
                  return labels[selected[0]] || selected[0];
                }
                return `${selected.length} wybrane`;
              }}
            >
              <MenuItem value="5">
                <Checkbox checked={companyCredibility.indexOf('5') > -1} />
                <ListItemText primary="5 - Współpraca wskazana" />
              </MenuItem>
              <MenuItem value="4">
                <Checkbox checked={companyCredibility.indexOf('4') > -1} />
                <ListItemText primary="4 - Zalecamy współpracę" />
              </MenuItem>
              <MenuItem value="3">
                <Checkbox checked={companyCredibility.indexOf('3') > -1} />
                <ListItemText primary="3 - Zalecamy ostrożność" />
              </MenuItem>
              <MenuItem value="2">
                <Checkbox checked={companyCredibility.indexOf('2') > -1} />
                <ListItemText primary="2 - Zalecamy szczególną ostrożność" />
              </MenuItem>
              <MenuItem value="none">
                <Checkbox checked={companyCredibility.indexOf('none') > -1} />
                <ListItemText primary="Brak danych" />
              </MenuItem>
            </Select>
          </FormControl>

          <Typography sx={{ fontWeight: 500, fontSize: 14, mt: 2 }}>
            Zgody marketingowe
          </Typography>

          <FormControl fullWidth size="small" sx={{ mt: 0.5 }}>
            <InputLabel id="consent-email-filter-label" sx={{ fontSize: '0.875rem' }}>
              Zgoda na email
            </InputLabel>
            <Select
              labelId="consent-email-filter-label"
              value={consentEmail}
              onChange={handleConsentEmailChange}
              label="Zgoda na email"
              sx={{ fontSize: '0.875rem' }}
            >
              <MenuItem value="">
                <em>Wszystkie</em>
              </MenuItem>
              <MenuItem value="true">TAK</MenuItem>
              <MenuItem value="false">NIE</MenuItem>
            </Select>
          </FormControl>

          <FormControl fullWidth size="small" sx={{ mt: 0.5 }}>
            <InputLabel id="consent-phone-filter-label" sx={{ fontSize: '0.875rem' }}>
              Zgoda na telefon
            </InputLabel>
            <Select
              labelId="consent-phone-filter-label"
              value={consentPhone}
              onChange={handleConsentPhoneChange}
              label="Zgoda na telefon"
              sx={{ fontSize: '0.875rem' }}
            >
              <MenuItem value="">
                <em>Wszystkie</em>
              </MenuItem>
              <MenuItem value="true">TAK</MenuItem>
              <MenuItem value="false">NIE</MenuItem>
            </Select>
          </FormControl>

          <FormControl fullWidth size="small" sx={{ mt: 0.5 }}>
            <InputLabel id="consent-group-company-filter-label" sx={{ fontSize: '0.875rem' }}>
              Zgoda na grupę firm
            </InputLabel>
            <Select
              labelId="consent-group-company-filter-label"
              value={consentGroupCompany}
              onChange={handleConsentGroupCompanyChange}
              label="Zgoda na grupę firm"
              sx={{ fontSize: '0.875rem' }}
            >
              <MenuItem value="">
                <em>Wszystkie</em>
              </MenuItem>
              <MenuItem value="true">TAK</MenuItem>
              <MenuItem value="false">NIE</MenuItem>
            </Select>
          </FormControl>

          <Typography sx={{ fontWeight: 500, fontSize: 14, mt: 2 }}>
            Błędy
          </Typography>

          <FormControl fullWidth size="small" sx={{ mt: 0.5 }}>
            <InputLabel id="missing-nip-filter-label" sx={{ fontSize: '0.875rem' }}>
              Brak NIP
            </InputLabel>
            <Select
              labelId="missing-nip-filter-label"
              value={missingNip}
              onChange={handleMissingNipChange}
              label="Brak NIP"
              sx={{ fontSize: '0.875rem' }}
            >
              <MenuItem value="">
                <em>Wszystkie</em>
              </MenuItem>
              <MenuItem value="true">TAK</MenuItem>
              <MenuItem value="false">NIE</MenuItem>
            </Select>
          </FormControl>

          <FormControl fullWidth size="small" sx={{ mt: 0.5 }}>
            <InputLabel id="missing-email-filter-label" sx={{ fontSize: '0.875rem' }}>
              Brak email
            </InputLabel>
            <Select
              labelId="missing-email-filter-label"
              value={missingEmail}
              onChange={handleMissingEmailChange}
              label="Brak email"
              sx={{ fontSize: '0.875rem' }}
            >
              <MenuItem value="">
                <em>Wszystkie</em>
              </MenuItem>
              <MenuItem value="true">TAK</MenuItem>
              <MenuItem value="false">NIE</MenuItem>
            </Select>
          </FormControl>

          <FormControl fullWidth size="small" sx={{ mt: 0.5 }}>
            <InputLabel id="missing-pwg-filter-label" sx={{ fontSize: '0.875rem' }}>
              Brak danych PWG
            </InputLabel>
            <Select
              labelId="missing-pwg-filter-label"
              value={missingPwg}
              onChange={handleMissingPwgChange}
              label="Brak danych PWG"
              sx={{ fontSize: '0.875rem' }}
            >
              <MenuItem value="">
                <em>Wszystkie</em>
              </MenuItem>
              <MenuItem value="true">TAK</MenuItem>
              <MenuItem value="false">NIE</MenuItem>
            </Select>
          </FormControl>

          <FormControl fullWidth size="small" sx={{ mt: 0.5 }}>
            <InputLabel id="grace-period-filter-label" sx={{ fontSize: '0.875rem' }}>
              W karencji (NIP zablokowany)
            </InputLabel>
            <Select
              labelId="grace-period-filter-label"
              value={gracePeriod}
              onChange={handleGracePeriodChange}
              label="W karencji (NIP zablokowany)"
              sx={{ fontSize: '0.875rem' }}
            >
              <MenuItem value="">
                <em>Wszystkie</em>
              </MenuItem>
              <MenuItem value="true">TAK</MenuItem>
              <MenuItem value="false">NIE</MenuItem>
            </Select>
          </FormControl>

          <Divider sx={{ my: 1.5 }} />

          {/* Sekcja: Filtry po przetargu */}
          <Typography variant="subtitle2" sx={{ fontWeight: 600, color: 'text.primary', mb: 0.5 }}>
            Filtry po przetargu
          </Typography>

          <FilterLiveSearch key={`tender_notice_number-${filterKey}`} source="tender_notice_number" label="Numer ogłoszenia" />
          <FilterLiveSearch key={`tender_title-${filterKey}`} source="tender_title" label="Tytuł przetargu" />

          <FormControl fullWidth size="small" sx={{ mt: 1 }}>
            <InputLabel id="nwu-filter-label" sx={{ fontSize: '0.875rem' }}>
              {translate('resources.tenders.fields.is_nwu', 'Czy jest NWU')}
            </InputLabel>
            <Select
              labelId="nwu-filter-label"
              value={nwu}
              onChange={handleNwuChange}
              label={translate('resources.tenders.fields.is_nwu', 'Czy jest NWU')}
              sx={{ fontSize: '0.875rem' }}
            >
              <MenuItem value="">
                <em>Wszystkie</em>
              </MenuItem>
              <MenuItem value="true">{translate('ra.boolean.true', 'Tak')}</MenuItem>
              <MenuItem value="false">{translate('ra.boolean.false', 'Nie')}</MenuItem>
            </Select>
          </FormControl>

          <FormControl fullWidth size="small" sx={{ mt: 1 }}>
            <InputLabel id="wadium-filter-label" sx={{ fontSize: '0.875rem' }}>
              {translate('resources.tenders.fields.is_wadium', 'Czy jest Wadium')}
            </InputLabel>
            <Select
              labelId="wadium-filter-label"
              value={wadium}
              onChange={handleWadiumChange}
              label={translate('resources.tenders.fields.is_wadium', 'Czy jest Wadium')}
              sx={{ fontSize: '0.875rem' }}
            >
              <MenuItem value="">
                <em>Wszystkie</em>
              </MenuItem>
              <MenuItem value="true">{translate('ra.boolean.true', 'Tak')}</MenuItem>
              <MenuItem value="false">{translate('ra.boolean.false', 'Nie')}</MenuItem>
            </Select>
          </FormControl>
          
          <Divider sx={{ my: 1 }} />
          
          <FilterLiveSearch key={`current_block-${filterKey}`} source="current_block" label="Aktualny proces" />
          <FilterLiveSearch key={`error_message-${filterKey}`} source="error_message" label="Komunikat błędu" />
          
          <Divider sx={{ my: 1 }} />
          
          <Typography sx={{ fontWeight: 500, fontSize: 14, mt: 1 }}>
            Data oferty
          </Typography>
          <MuiTextField
            label="Od"
            type="date"
            value={offerDateFrom}
            onChange={e => handleOfferDateChange('from', e.target.value)}
            InputLabelProps={{ shrink: true }}
            fullWidth
            margin="dense"
            size="small"
            variant="outlined"
          />
          <MuiTextField
            label="Do"
            type="date"
            value={offerDateTo}
            onChange={e => handleOfferDateChange('to', e.target.value)}
            InputLabelProps={{ shrink: true }}
            fullWidth
            margin="dense"
            size="small"
            variant="outlined"
          />
          
          <Typography sx={{ fontWeight: 500, fontSize: 14, mt: 1 }}>
            Data aktualizacji
          </Typography>
          <MuiTextField
            label="Od"
            type="date"
            value={from}
            onChange={e => handleDateChange('from', e.target.value)}
            InputLabelProps={{ shrink: true }}
            fullWidth
            margin="dense"
            size="small"
            variant="outlined"
          />
          <MuiTextField
            label="Do"
            type="date"
            value={to}
            onChange={e => handleDateChange('to', e.target.value)}
            InputLabelProps={{ shrink: true }}
            fullWidth
            margin="dense"
            size="small"
            variant="outlined"
          />
          <MuiButton 
            variant="outlined" 
            size="small" 
            onClick={handleResetFilters}
            sx={{ mt: 3, mb: 1 }}
            fullWidth
          >
            Wyczyść filtry
          </MuiButton>
        </Stack>
      </CardContent>
    </Card>
  );
};

export const FailedBidderWorkflowList = props => {
    const translate = useTranslate();
    return (
        <StyledList 
            {...props}
            actions={<ListActions />}
            perPage={25}
            sort={{ field: 'updated_at', order: 'DESC' }}
            aside={<FailedBidderWorkflowFilterSidebar />}
            title="Nieudane workflow oferentów"
        >
            <div style={{ width: '100%', overflowX: 'auto' }}>
                <DatagridConfigurable 
                    sx={{ 
                        minWidth: 'auto',
                        '& .MuiTableCell-root': {
                            padding: '8px 12px !important',
                            verticalAlign: 'top !important',
                            minHeight: '80px !important',
                            height: 'auto !important',
                            overflow: 'visible !important'
                        },
                        '& .MuiTableRow-root': {
                            minHeight: '80px !important',
                            height: 'auto !important'
                        },
                        '& .column-id': { minWidth: '60px', maxWidth: '60px' },
                        '& .column-bidder_info': { minWidth: '180px', maxWidth: '220px' },
                        '& .column-bidder_notes': { minWidth: '200px', maxWidth: '300px' },
                        '& .column-tender_info': { minWidth: '220px', maxWidth: '280px' },
                        '& .column-tender_deadline': { minWidth: '140px', maxWidth: '140px' },
                        '& .column-marketing_consents': { minWidth: '180px', maxWidth: '220px' },
                        '& .column-offer_status': { minWidth: '100px', maxWidth: '120px' },
                        '& .column-workflow_info': { minWidth: '200px', maxWidth: '280px' },
                        '& .column-error_message': { minWidth: '280px', maxWidth: '380px' },
                        '& .column-updated_at': { minWidth: '140px', maxWidth: '140px' },
                        '& .column-archive': { minWidth: '80px', maxWidth: '80px' }
                    }}
                    rowClick={false}
                    bulkActionButtons={false}
                    omit={[
                        'master_id',
                        'next_run_at',
                        'steps',
                        'tender_value',
                        'retries',
                        'bidder_name',
                        'bidder_tax_number', 
                        'bidder_email',
                        'bidder_notes',
                        'tender_notice_number',
                        'tender_title',
                        'current_block',
                        'status',
                        'consent_email',
                        'consent_phone',
                        'consent_group_company'
                    ]}
                >
                    <TextField source="id" label="ID" />
                    <BidderInfoField source="bidder_info" label="Oferent" />
                    <FunctionField 
                        source="bidder_notes" 
                        label="Notatka z oferenta"
                        render={(record) => (
                            <Typography 
                                sx={{ 
                                    fontSize: '0.875rem',
                                    whiteSpace: 'pre-wrap',
                                    wordBreak: 'break-word',
                                    maxWidth: '300px'
                                }}
                            >
                                {record?.bidder_notes || '-'}
                            </Typography>
                        )}
                    />
                    <TenderInfoField source="tender_info" label="Przetarg" />
                    <DateField source="tender_deadline" label="Termin składania ofert" showTime />
                    <FunctionField 
                        source="marketing_consents" 
                        label="Zgody marketingowe" 
                        render={() => <MarketingConsentsField />}
                    />
                    <OfferStatusField source="offer_status" label="Status" />
                    <FunctionField 
                        source="workflow_info" 
                        label="Status procesu" 
                        sortBy="status"
                        render={() => <WorkflowInfoField />}
                    />
                    <ErrorMessageField source="error_message" label="Komunikat błędu" />
                    <DateField source="updated_at" label="Ostatnia aktualizacja" showTime />
                    <ArchiveButton />
                </DatagridConfigurable>
            </div>
        </StyledList>
    );
};

export default {
    list: FailedBidderWorkflowList,
    hasList: true,
    hasShow: false,
    hasEdit: false,
    hasCreate: false,
};
